import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import {
  ReadAdvancedSearchResultsApiArg,
  SearchFilterCondition,
  SearchUserHit,
  User,
} from 'lib-shared-types-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMount, useUpdateEffect } from 'react-use';

import { AwayStatus, presenceApi } from 'apis/presence';
import { searchApi } from 'apis/search';
import { userModalActions } from 'components/usersModal';
import {
  useMembersWithPresence,
  useOrganizationPresenceSubscription,
  usePresenceOverlaySync,
} from 'hooks/usePresenceCacheSync';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useFavoriteAgents } from 'utils/useFavoriteAgents';

import { type ActiveFilters, type Status } from '../components/searchControls';

type AvailableFilters = {
  status: { [K in Status]: SearchFilterCondition[] };
  team: (teamIds: string[]) => SearchFilterCondition[];
  agent: (agentIds: string[]) => SearchFilterCondition[];
  role: (roleIds: string[]) => SearchFilterCondition[];
};

type UseTeamTableDataParams = {
  page: number;
  activeFilters: ActiveFilters;
};

type UseTeamTableDataReturn = {
  ownId: string | undefined;
  favorites: readonly string[];
  membersWithPresence: readonly SearchUserHit[];
  memberCount: number | undefined;
  pageCount: number | undefined;
  loading: boolean;
  isManualRefresh: boolean;
  handleRefreshMembers: () => void;
  toggleFavorite: (isFavorite: boolean, agentId: string) => () => void;
  showUserModal: (user: Partial<User>) => void;
  hasOrganizationMemberAccess: boolean;
  hasPresenceAccess: boolean;
  awayStatuses: AwayStatus[];
};

export const useTeamTableData = ({
  page,
  activeFilters,
}: UseTeamTableDataParams): UseTeamTableDataReturn => {
  const dispatch = useAppDispatch();

  // Redux state
  const ownId = useAppSelector((state) => state.settings.profile.profile?.id);

  const {
    favoriteAgentIds: favorites,
    addFavorite,
    removeFavorite,
  } = useFavoriteAgents();

  const showUserModal = useCallback(
    (user: Partial<User>): void => {
      dispatch(userModalActions.show({ user }));
    },
    [dispatch],
  );

  const hasOrganizationMemberAccess = useHasAccess('organization.member');
  const hasPresenceAccess = useHasAccess(
    [
      'organization.member.presence.status',
      'organization.member.presence.working_media',
    ],
    { strategy: 'any' },
  );

  const flags = useFeatureFlag(['custom-away-status']);

  const [isManualRefresh, setIsManualRefresh] = useState(false);

  const availableFilters: AvailableFilters = useMemo(
    () => ({
      status: {
        working: [
          {
            field: 'is_working',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        away: [
          {
            field: 'is_working',
            operator: 'equals',
            values: [{ label: '', value: 'false' }],
          },
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        online: [
          {
            field: 'is_online',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        offline: [
          {
            field: 'is_online',
            operator: 'not_equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        'wrap-up': [
          {
            field: 'has_acw',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        favorite: [
          {
            field: 'id',
            operator: 'is_one',
            values: favorites.map((id) => ({
              label: '',
              value: id,
            })),
          },
        ],
        assigned: [
          {
            field: 'is_assigned',
            operator: 'equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
        unassigned: [
          {
            field: 'is_assigned',
            operator: 'not_equals',
            values: [{ label: '', value: 'true' }],
          },
        ],
      },
      team: (teamIds: string[]) => [
        {
          field: 'teams',
          operator: 'is_one',
          values: teamIds.map((teamId) => ({ label: '', value: teamId })),
        } satisfies SearchFilterCondition,
      ],
      agent: (agentIds) => [
        {
          field: 'id',
          operator: 'is_one',
          values: agentIds.map((agentId) => ({ label: '', value: agentId })),
        } satisfies SearchFilterCondition,
      ],
      role: (roleIds) => [
        {
          field: 'roles',
          operator: 'is_one',
          values: roleIds.map((roleId) => ({ label: '', value: roleId })),
        } satisfies SearchFilterCondition,
      ],
    }),
    [favorites],
  );

  const queryConditions: SearchFilterCondition[] = useMemo(
    () => [
      ...(activeFilters.status
        ? availableFilters.status[activeFilters.status]
        : ([] as SearchFilterCondition[])),
      ...(activeFilters.team.length
        ? availableFilters.team(activeFilters.team)
        : []),
      ...(activeFilters.agent.length
        ? availableFilters.agent(activeFilters.agent)
        : []),
      ...(activeFilters.role.length
        ? availableFilters.role(activeFilters.role)
        : []),
    ],
    [activeFilters, availableFilters],
  );

  const searchQueryParams: ReadAdvancedSearchResultsApiArg = useMemo(
    () => ({
      params: {
        scope: 'users',
        query: '',
        filters: {
          strategy: 'all',
          conditions: [
            {
              field: 'user_type',
              operator: 'is_one',
              values: [{ label: '', value: 'member' }],
            },
            ...queryConditions,
          ],
        },
        sort: { field: 'display_name', order: 'asc' },
        enrichmentSpec: ['workload_config'],
        strict: true,
        page,
      },
    }),
    [page, queryConditions],
  );

  const [
    triggerSearch,
    { members, memberCount, pageCount, isLoading, isFetching },
  ] = searchApi.useLazyReadAdvancedSearchResultsQuery({
    selectFromResult: ({ data, isLoading, isFetching }) => {
      const filteredResult = (data?.data.results || []).filter(
        (item) => item.hits_type === 'user',
      );
      const agentsResult = filteredResult.length > 0 ? filteredResult[0] : null;

      return {
        members: agentsResult ? agentsResult.hits : [],
        memberCount: agentsResult?.nb_hits,
        pageCount: agentsResult?.nb_pages,
        isLoading,
        isFetching,
      };
    },
  });

  // Initial load - fresh fetch
  useMount(() => {
    triggerSearch(searchQueryParams, false);
  });

  // Filter changes - fresh fetch (invalidate cache)
  useUpdateEffect(() => {
    triggerSearch(searchQueryParams, false);
  }, [queryConditions]);

  // Page changes - use cache if available
  useUpdateEffect(() => {
    triggerSearch(searchQueryParams, true);
  }, [page]);

  const loading = isLoading || isFetching;

  const handleRefreshMembers = useCallback(() => {
    setIsManualRefresh(true);
    triggerSearch(searchQueryParams, false);
  }, [triggerSearch, searchQueryParams]);

  useEffect(() => {
    if (!loading && isManualRefresh) {
      setIsManualRefresh(false);
    }
  }, [loading, isManualRefresh]);

  usePresenceOverlaySync();
  const membersWithPresence = useMembersWithPresence(members);

  useOrganizationPresenceSubscription();

  const { awayStatuses } = presenceApi.useReadAwayStatusesQuery(
    { isDeactivated: false },
    {
      skip: !flags.customAwayStatus,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        awayStatuses: data?.data.customAwayStatuses ?? [],
      }),
    },
  );

  const toggleFavorite = useCallback(
    (isFavorite: boolean, agentId: string) => () => {
      ampli.teamFavoriteToggled({ value: !isFavorite });
      if (isFavorite) {
        removeFavorite(agentId);
      } else {
        addFavorite(agentId);
      }
    },
    [addFavorite, removeFavorite],
  );

  return {
    ownId,
    favorites,
    membersWithPresence,
    memberCount,
    pageCount,
    loading,
    isManualRefresh,
    handleRefreshMembers,
    toggleFavorite,
    showUserModal,
    hasOrganizationMemberAccess,
    hasPresenceAccess,
    awayStatuses,
  };
};
